// recovery-ticket-shape.ts
//
// Orchestrator recovery (3.40.0): the fixed shape of a controller-authored
// recovery ticket (D2.3 — same fields as the redirect decision ticket in
// redirect-text-to-decision-ticket.ts) and its journal op ids. A recovery
// ticket is recognised by its ONE `sources` label plus the ledger
// (controller-redirect-decision-ticket.ts) — never by the label alone.
import { type Ticket, TicketSchema } from "./schemas/ticket-schema.js";

export const RECOVERY_TICKET_TRIGGERS = ["counted_cap", "timeout_split", "plan_blocked"] as const;
export type RecoveryTicketTrigger = (typeof RECOVERY_TICKET_TRIGGERS)[number];

export function recoverySourceLabel(sourceTicket: string, trigger: RecoveryTicketTrigger): string {
  return `recovery:${sourceTicket}:${trigger}`;
}

export function recoveryTicketTitle(sourceTicket: string, trigger: RecoveryTicketTrigger): string {
  return `Gỡ kẹt ${sourceTicket} (${trigger})`;
}

/** `recovery-<ticket>-<trigger>-<seq>`, retries `-r<n>` (n = 1, 2). */
export function recoveryTicketOpId(
  sourceTicket: string,
  trigger: RecoveryTicketTrigger,
  triggerSeq: number,
  attempt: number,
): string {
  const base = `recovery-${sourceTicket}-${trigger}-${triggerSeq}`;
  return attempt === 0 ? base : `${base}-r${attempt}`;
}

export function recoverySkipOpId(
  sourceTicket: string,
  trigger: string,
  triggerSeq: number,
): string {
  return `recovery-skip-${sourceTicket}-${trigger}-${triggerSeq}`;
}

/** The file at this id is (a try of) the recovery ticket for this label — not a ticket that took the id. */
export function isRecoveryTicketFor(ticket: Ticket | undefined, label: string): boolean {
  return ticket !== undefined && ticket.sources.length === 1 && ticket.sources[0] === label;
}

export function buildRecoveryTicket(input: {
  id: string;
  sourceTicket: string;
  trigger: RecoveryTicketTrigger;
  outcome: string;
}): Ticket {
  return TicketSchema.parse({
    id: input.id,
    revision: 1,
    title: recoveryTicketTitle(input.sourceTicket, input.trigger),
    kind: "decision",
    executor: "claude",
    stage: "DELIVERING",
    slice_id: "recovery",
    depends_on: [],
    command_adapter: "decision-v1",
    plan_path: ".avatar/PLAN.md",
    phase_path: ".avatar/plans/bootstrap.md",
    allowed_paths: [],
    // Same "<kind>-bootstrap" convention as the redirect decision ticket.
    verification_recipe: "decision-bootstrap",
    required_capabilities: [],
    sources: [recoverySourceLabel(input.sourceTicket, input.trigger)],
    outcome: input.outcome,
  });
}
